import '@fontsource/crimson-pro/400.css';
import '@fontsource/crimson-pro/500.css';
import '@fontsource/crimson-pro/600.css';
import '@fontsource/crimson-pro/700.css';
import '@fontsource/ibm-plex-sans/300.css';
import '@fontsource/ibm-plex-sans/400.css';
import '@fontsource/ibm-plex-sans/500.css';
import '@fontsource/ibm-plex-sans/600.css';
import '@fontsource/ibm-plex-sans/700.css';
import * as Sentry from '@sentry/react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { captureCampaignAttribution } from './lib/campaign-attribution';
import { captureReferral } from './lib/referral-capture';
import { installVersionChecks } from './lib/app-version';
import { CLIENT_VERSION } from './lib/config';
import { initializeApi } from './lib/platform-bridge';
import { dropStaleChunkEvent } from './lib/stale-chunk';
import { installUiUsage } from './lib/ui-usage';
import './styles/globals.css';

const sentryEnvironment = import.meta.env.VITE_ENVIRONMENT || 'local';

Sentry.init({
  enabled: import.meta.env.VITE_SENTRY_ENABLED !== 'false',
  dsn: sentryEnvironment === 'local' ? undefined : 'https://010d00913a3863f19434acb044b51a57@o4511145289711617.ingest.de.sentry.io/4511145333227600',
  environment: sentryEnvironment,
  release: CLIENT_VERSION,
  integrations: [Sentry.browserTracingIntegration()],
  // Do not send PII (IP addresses, user data) to Sentry
  sendDefaultPii: false,
  beforeSend: dropStaleChunkEvent,
});

// Redirect Firebase default URL to canonical production domain.
// bill-prod.web.app is not registered in Azure as a valid OAuth redirect URI - only app.bill.co.za is.
if (window.location.hostname === 'bill-prod.web.app') {
  const canonical = new URL(window.location.href);
  canonical.hostname = 'app.bill.co.za';
  window.location.replace(canonical.toString());
} else {
  initializeApi();
  // After initializeApi: the usage channel reads the session token from the API bridge.
  installUiUsage();
  // Before the first render: /login?provider= leaves for the OAuth provider on mount.
  captureCampaignAttribution(window.location.search);
  captureReferral(window.location.search);
  installVersionChecks();

  ReactDOM.createRoot(document.getElementById('app')!).render(
    <React.StrictMode>
      {/* Navigations run outside a transition so a page whose code is still
          loading shows its loading state at once; inside one, React keeps the
          old page on screen with no sign the click registered. */}
      <BrowserRouter useTransitions={false}>
        <App />
      </BrowserRouter>
    </React.StrictMode>,
  );
}
